import {
  type ReferenceImageUrlHints,
  resolveReferenceImageDisplayUrl as resolveReferenceImageLink,
} from '../lib/referenceImageUrl'
import { apiRequest } from './client'

export { resolveAbsoluteHttpImageUrl, resolveGalleryRemoteIngestUrl } from '../lib/referenceImageIngestUrl'
export type { ReferenceImageLinkKind, ReferenceImageUrlHints } from '../lib/referenceImageUrl'
export { classifyReferenceImageLink } from '../lib/referenceImageUrl'

export type ReferenceUploadResult = {
  url: string
  name: string
  size: number
  contentType: string
  publicId: string
  bucket: string
  objectKey: string
}

export type ReferenceUploadMeta = {
  prompt?: string
  roundId?: string
  genItemId?: string
  modelLabel?: string
  aspectLabel?: string
  sourceCreatedAt?: string
  /** 与当轮参考图一致的 URL 列表，序列化为 JSON 数组提交 */
  referenceImageUrls?: string[]
}

/**
 * 将接口返回的相对路径转为浏览器可拉取的绝对 URL（上传映射为站点根路径 /uploads/...）
 * 仅用于明确为应用内 /uploads 的上传结果；七牛图库请用 {@link resolveReferenceImageDisplayUrl}。
 */
export function absolutePublicUploadUrl(relativeOrAbsolute: string): string {
  return resolveReferenceImageLink(relativeOrAbsolute, { bucket: 'local' })
}

/**
 * 列表/灯箱展示：按 app-hosted / remote-cdn 区分，避免开发模式把 CDN 链到 :5173 或 :8082。
 */
export function resolveReferenceImageDisplayUrl(
  raw: string,
  hints?: ReferenceImageUrlHints,
): string {
  return resolveReferenceImageLink(raw, hints)
}

function appendReferenceUploadFormFields(body: FormData, meta?: ReferenceUploadMeta) {
  if (meta?.prompt !== undefined && meta.prompt !== null) body.append('prompt', meta.prompt)
  if (meta?.roundId !== undefined && meta.roundId !== null) body.append('roundId', meta.roundId)
  if (meta?.genItemId !== undefined && meta.genItemId !== null) body.append('genItemId', meta.genItemId)
  if (meta?.modelLabel !== undefined && meta.modelLabel !== null) body.append('modelLabel', meta.modelLabel)
  if (meta?.aspectLabel !== undefined && meta.aspectLabel !== null) body.append('aspectLabel', meta.aspectLabel)
  if (meta?.sourceCreatedAt !== undefined && meta.sourceCreatedAt !== null) body.append('sourceCreatedAt', meta.sourceCreatedAt)
  if (meta?.referenceImageUrls?.length) {
    body.append('referenceImageUrls', JSON.stringify(meta.referenceImageUrls))
  }
}

/**
 * 【本地磁盘上传】参考图：直接上传到服务器本地磁盘
 * - 存储路径：由服务器配置 `glyphicai.upload.root` 决定（本地文件系统）
 * - 适用场景：创作页面的参考图上传（临时/会话级）
 * - 限制：`folderPath` 必须为「上传的参考图」及其子路径
 * - 后端端点：POST /v1/uploads/references
 */
export async function uploadReferenceImageToDisk(
  file: File,
  folderPath: string,
  meta?: ReferenceUploadMeta,
): Promise<ReferenceUploadResult> {
  const body = new FormData()
  body.append('file', file)
  appendReferenceUploadFormFields(body, meta)
  const q = new URLSearchParams({ folderPath: folderPath.trim() })
  return apiRequest<ReferenceUploadResult>(`/v1/uploads/references?${q.toString()}`, {
    method: 'POST',
    body,
  })
}

/** @deprecated 请使用 uploadReferenceImageToDisk 以明确存储位置 */
export const uploadReferenceImage = uploadReferenceImageToDisk

/**
 * 【七牛云上传】图库图片：上传到七牛云 CDN
 * - 存储位置：七牛云对象存储（云端）
 * - 适用场景：用户图库、长期保存的作品
 * - 限制：`folderPath` 为图库虚拟路径（根目录传空串），禁止传「上传的参考图」前缀
 * - 后端端点：POST /v1/uploads/gallery
 */
export async function uploadGalleryImageToCloud(
  file: File,
  folderPath: string,
  meta?: ReferenceUploadMeta,
): Promise<ReferenceUploadResult> {
  const body = new FormData()
  body.append('file', file)
  appendReferenceUploadFormFields(body, meta)
  const query = folderPath.trim()
    ? `?${new URLSearchParams({ folderPath: folderPath.trim() }).toString()}`
    : ''
  return apiRequest<ReferenceUploadResult>(`/v1/uploads/gallery${query}`, {
    method: 'POST',
    body,
  })
}

/** @deprecated 请使用 uploadGalleryImageToCloud 以明确存储位置 */
export const uploadGalleryImage = uploadGalleryImageToCloud

/**
 * 【七牛云上传】服务端拉取外链后写入图库（云端）
 * - 存储位置：七牛云对象存储（云端）
 * - 适用场景：将外部图片保存到用户图库
 * - 后端端点：POST /v1/uploads/gallery/from-remote
 */
export async function uploadGalleryImageFromRemoteUrlToCloud(
  absoluteImageUrl: string,
  displayBaseName: string,
  folderPath: string,
  meta?: ReferenceUploadMeta,
): Promise<ReferenceUploadResult> {
  return apiRequest<ReferenceUploadResult>(`/v1/uploads/gallery/from-remote`, {
    method: 'POST',
    body: JSON.stringify({
      imageUrl: absoluteImageUrl,
      folderPath: folderPath.trim(),
      displayBaseName: displayBaseName.trim().slice(0, 160),
      prompt: meta?.prompt ?? undefined,
      roundId: meta?.roundId ?? undefined,
      genItemId: meta?.genItemId ?? undefined,
      modelLabel: meta?.modelLabel ?? undefined,
      aspectLabel: meta?.aspectLabel ?? undefined,
      sourceCreatedAt: meta?.sourceCreatedAt ?? undefined,
      referenceImageUrls: meta?.referenceImageUrls?.length
        ? JSON.stringify(meta.referenceImageUrls)
        : undefined,
    }),
  })
}

/**
 * 【本地磁盘上传】参考图：从远程 URL（七牛 CDN / 图库 / 外链）拉取后存到本地 uploads/references。
 * 创作页从图库选参考图时调用，确保 referenceAbsoluteUrls 里是稳定的本地 /uploads/... URL（永不过期）。
 * 后端端点：POST /v1/uploads/references/from-remote
 */
export async function uploadReferenceImageFromRemoteToDisk(
  remoteUrl: string,
  displayBaseName?: string,
  folderPath?: string,
): Promise<ReferenceUploadResult> {
  return apiRequest<ReferenceUploadResult>('/v1/uploads/references/from-remote', {
    method: 'POST',
    body: JSON.stringify({
      imageUrl: remoteUrl,
      folderPath: folderPath?.trim() || undefined,
      displayBaseName: displayBaseName?.trim() || undefined,
    }),
  })
}

/** @deprecated 请使用 uploadGalleryImageFromRemoteUrlToCloud 以明确存储位置 */
export const uploadGalleryImageFromRemoteUrl = uploadGalleryImageFromRemoteUrlToCloud
